import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { searchAPI, irregularityAPI } from '../../services/api';

export interface SearchFilters {
  [key: string]: any;
}

export interface SearchResult {
  id: string;
  contract_id?: string;  // Contract/Award ID
  title: string;
  description: string;
  amount: number;
  vendor?: string;
  vendor_name?: string;  // For backward compatibility
  agency: string;
  date: string;
  location?: {
    state?: string;
    city?: string;
    zip?: string;
  };
  url?: string;
  usaspending_permalink?: string;
  sam_link?: string;
  fraud_risk_level: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'LEGITIMATE' | 'UNKNOWN';
  fraud_risk_message: string;
  fraud_risk_factors: string[];
  fraud_analysis?: {
    risk_level: string;
    likelihood: string;
    legitimacy_score: number;
    suspicion_score: number;
    net_score: number;
    flags: string[];
    reasoning: string[];
    legitimate_indicators: string[];
    suspicious_indicators: string[];
    requires_investigation: boolean;
    recommendation: string;
  };
  irregularity_indicators?: {
    [key: string]: boolean;
  };
  irregularity_score?: number;
  number_of_offers_received?: number;
  total_obligated_amount?: number;
  disaster_emergency_fund_codes?: string;
  // Solicitation and competition information
  solicitation_info?: {
    code: string;
    description: string;
    competition_type: string;
  };
  award_details?: {
    award_id: string;
    piid: string;
    award_type: string;
    contracting_agency: string;
    funding_agency: string;
    start_date: string;
    end_date: string;
    performance_location: string;
  };
  // Add fields for timing and emergency analysis
  timing_analysis?: {
    risk_score: number;
    risk_level: string;
    anomalies: string[];
    patterns: any[];
    explanation: string;
  };
  emergency_analysis?: {
    risk_score: number;
    risk_level: string;
    misuse_indicators: string[];
    patterns: any[];
    explanation: string;
    emergency_type?: string;
  };
  // Add fields that come from USASpending data
  prime_award_base_transaction_description?: string;
  recipient_name?: string;
  current_total_value_of_award?: number;
  award_base_action_date?: string;
  awarding_agency_name?: string;
}

// Vendor-focused result for vendor pattern searches (vendor-concentration, split-purchase, etc.)
export interface VendorResult {
  vendor_name: string;
  vendor_location?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  vendor_uei?: string;
  total_amount: number;
  contract_count: number;
  risk_score?: number;
  contracts: Array<{
    award_id?: string;
    date: string;
    amount: number;
    agency: string;
    office?: string;
    award_description?: string;
    description?: string;
    naics_code?: string;
    naics_description?: string;
    performance_location?: string;
    competition?: string;
    offers_received?: number;
  }>;
  agencies: string[];
  date_range?: {
    min: string;
    max: string;
  };
  // Pattern-specific risk data
  split_purchase_risk?: {
    risk_level: string;
    patterns: Array<{
      contracts: Array<{
        award_id: string;
        date: string;
        amount: number;
      }>;
      total_amount: number;
      contract_count: number;
      time_span_hours: number;
      nearest_threshold?: {
        name: string;
        value: number;
        abbreviation: string;
        proximity: number;
      };
      same_agency: boolean;
    }>;
  };
  concentration_risk?: {
    risk_level: string;
    market_share_percentage: number;
    contract_share_percentage: number;
    competitor_count: number;
    dominant_naics?: {
      code: string;
      percentage: number;
    };
  };
  repeat_award_risk?: {
    risk_level: string;
    patterns: Array<{
      product_service: string;
      contract_count: number;
      frequency_per_month: number;
      time_pattern: string;
      average_competition: number;
      total_amount: number;
    }>;
  };
}

export interface SearchState {
  query: string;
  results: SearchResult[];
  vendorResults: VendorResult[];  // Vendor-specific results for pattern searches
  loading: boolean;
  error: string | null;
  currentType: string;
  useRag: boolean;
  filters: SearchFilters;
  totalResults: number;
  explanation: string;
  processingTime: number;
  sourcesSearched: string[];
  investigationSummary?: {
    [key: string]: any;
  };
}

const initialState: SearchState = {
  query: '',
  results: [],
  vendorResults: [],
  loading: false,
  error: null,
  currentType: '',
  useRag: true,
  filters: {},
  totalResults: 0,
  explanation: '',
  processingTime: 0,
  sourcesSearched: [],
};

// Async thunks for API calls
export const performStandardSearch = createAsyncThunk(
  'search/performStandardSearch',
  async (params: { query: string; useRag?: boolean; filters?: SearchFilters }) => {
    const response = await searchAPI.standardSearch(params.query, {
      use_rag: params.useRag !== undefined ? params.useRag : true,
      filters: params.filters,
    });
    return response;
  }
);

export const performIrregularitySearch = createAsyncThunk(
  'search/performIrregularitySearch',
  async (params: {
    irregularityType: string;
    query: string;
    maxResults?: number;
    filters?: SearchFilters;
  }) => {
    const response = await irregularityAPI.searchByType(
      params.irregularityType,
      params.query,
      {
        max_results: params.maxResults || 30,
        filters: params.filters,
      }
    );
    return response;
  }
);

export const getIrregularityTypes = createAsyncThunk(
  'search/getIrregularityTypes',
  async () => {
    const response = await irregularityAPI.getTypes();
    return response.irregularity_types;
  }
);

// Unified search thunk that can handle both standard and irregularity searches
export const searchAsync = createAsyncThunk(
  'search/searchAsync',
  async (params: {
    query: string;
    sources?: string[];
    max_results?: number;
    include_explanation?: boolean;
    filters?: SearchFilters;
    global_search?: boolean;
    use_rag?: boolean;
    irregularity_type?: string;
  }, { rejectWithValue }) => {
    // If irregularity_type is specified, use irregularity search
    if (params.irregularity_type) {
      const response = await irregularityAPI.searchByType(
        params.irregularity_type,
        params.query,
        {
          max_results: params.max_results || 50,
          filters: params.filters,
        }
      );
      return {
        ...response,
        irregularity_type: params.irregularity_type
      };
    } else {
      // Otherwise use standard search
      const response = await searchAPI.standardSearch(params.query, {
        use_rag: params.use_rag !== undefined ? params.use_rag : true,
        filters: params.filters,
        sources: params.sources,
        max_results: params.max_results,
        include_explanation: params.include_explanation,
        global_search: params.global_search,
      });
      return {
        ...response,
        irregularity_type: null
      };
    }
  }
);

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    setCurrentType: (state, action: PayloadAction<string>) => {
      state.currentType = action.payload;
    },
    setUseRag: (state, action: PayloadAction<boolean>) => {
      state.useRag = action.payload;
    },
    setFilters: (state, action: PayloadAction<SearchFilters>) => {
      state.filters = action.payload;
    },
    clearResults: (state) => {
      state.results = [];
      state.vendorResults = [];
      state.totalResults = 0;
      state.explanation = '';
      state.investigationSummary = undefined;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Standard search
      .addCase(performStandardSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(performStandardSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.results || [];
        state.totalResults = action.payload.total_results || 0;
        state.explanation = action.payload.explanation || '';
      })
      .addCase(performStandardSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Search failed';
        state.results = [];
      })
      // Irregularity search
      .addCase(performIrregularitySearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(performIrregularitySearch.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.results || [];
        state.totalResults = action.payload.total_found || action.payload.total_results || 0;
        state.explanation = action.payload.explanation || '';
        state.investigationSummary = action.payload.investigation_summary;
      })
      .addCase(performIrregularitySearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Irregularity search failed';
        state.results = [];
      })
      // Unified search
      .addCase(searchAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAsync.fulfilled, (state, action) => {
        state.loading = false;
        
        // Set the current type based on the search type
        state.currentType = action.payload.irregularity_type || '';
        
        // Detect if we received vendor-pattern results vs individual contract results
        const results = action.payload.results || [];

        // Vendor results detection - contracts array is UNIQUE to VendorResult
        // SearchResult never has a 'contracts' array property
        // This ensures standard search results are never misclassified as vendor results
        const hasVendorResults = results.length > 0 &&
          Array.isArray(results[0].contracts) &&
          typeof results[0].contract_count === 'number';
        
        if (hasVendorResults) {
          // Handle vendor-pattern results (vendor-concentration, split-purchase, repeat-awards)
          state.vendorResults = results as VendorResult[];
          state.results = []; // Clear individual results
        } else {
          // Handle individual contract results (standard searches, competition issues)
          state.results = results as SearchResult[];
          state.vendorResults = []; // Clear vendor results
        }
        
        state.totalResults = action.payload.total_found || action.payload.total_results || 0;
        state.explanation = action.payload.explanation || '';
        state.processingTime = action.payload.processing_time || 0;
        state.sourcesSearched = action.payload.sources_searched || [];
        state.investigationSummary = action.payload.investigation_summary;
      })
      .addCase(searchAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Search failed';
        state.results = [];
        state.vendorResults = [];
      });
  },
});

export const {
  setQuery,
  setCurrentType,
  setUseRag,
  setFilters,
  clearResults,
  clearError,
} = searchSlice.actions;

// Selectors
export const selectSearchState = (state: { search: SearchState }) => state.search;
export const selectSearchResults = (state: { search: SearchState }) => state.search.results;
export const selectVendorResults = (state: { search: SearchState }) => state.search.vendorResults;
export const selectSearchLoading = (state: { search: SearchState }) => state.search.loading;
export const selectSearchError = (state: { search: SearchState }) => state.search.error;
export const selectSearchQuery = (state: { search: SearchState }) => state.search.query;

export default searchSlice.reducer;